import React from "react";
import {Checkbox, Tooltip, OverlayTrigger, InputGroup} from "react-bootstrap";
import {Spinner} from "../spinner/Spinner";
import {LcdIcon} from "../icon/LcdIcon";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";

const MESSAGES = defineMessages({
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  },
  CLEAR: {
    id: "studio.property.edit.clear",
    defaultMessage: "Clear"
  }
});

interface BoolEditProps {
  name?: string;
  value: boolean;
  defaultValue?: boolean;
  onChange: (newValue: boolean) => void | Promise<void>;
  readOnly?: boolean;
}

interface BoolEditState {
  showClearButton: boolean;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR";
  submitError: Error;
  lastMouseOut: number;
}

class BoolEditComponent extends React.Component<BoolEditProps & InjectedIntlProps, BoolEditState> {
  _clearButtonTimeout: NodeJS.Timeout;

  constructor(props) {
    super(props);

    this.state = {
      showClearButton: false,
      submitState: "NOT_STARTED",
      submitError: null,
      lastMouseOut: 0,
    };
  }

  private valueChanged = () => {
    return this.updateValue(!this.props.value);
  }

  private updateValue = (newValue) => {
    const onChangeResult = this.props.onChange(newValue);

    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});
      const onChangeResultPromise = onChangeResult as Promise<void>;

      return onChangeResultPromise
          .then(() => {
            this.reset();
          })
          .catch((error) => {
            this.setState({submitState: "ERROR", submitError: error})

            throw error;
          });
    } else {
      this.reset();
    }
  };

  private reset = () => {
    this.setState({
      showClearButton: false,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = () => {
    // Remove this time hack when implementing a real solution for LF-2229
    if (Date.now() - this.state.lastMouseOut > 20) {
      clearTimeout(this._clearButtonTimeout);
      this._clearButtonTimeout = setTimeout(() => {
        this.setState({showClearButton: false});
      }, 2000);
      this.setState({showClearButton: true});
    }
  }

  handleMouseOut = () => {
    this.setState({lastMouseOut: Date.now()});
  }

  handleButtonMouseOut = () => {
    this.setState({showClearButton: false})
  }

  handleButtonMouseOver = () => {
    clearTimeout(this._clearButtonTimeout);
    this.setState({showClearButton: true});
  }

  handleButtonClick = () => {
    this.setState({showClearButton: false});
    return this.updateValue(null);
  }

  renderCheckbox = (inputValue: boolean) => {
    return <Checkbox data-cy={this.props["data-cy"]} inline={true} checked={inputValue}
                     onChange={this.valueChanged} onBlur={this.reset} disabled={this.props.readOnly}/>
  }

  renderInput = () => {
    const {intl, value, children, defaultValue} = this.props;
    const {showClearButton} = this.state;

    if (showClearButton) {
      return <InputGroup>
        {this.renderCheckbox(value)}
        {(value !== null) && <button
            onFocus={this.handleButtonMouseOver}
            onMouseOver={this.handleButtonMouseOver}
            onBlur={this.handleButtonMouseOut}
            onMouseOut={this.handleButtonMouseOut}
            onClick={this.handleButtonClick}
        >{intl.formatMessage(MESSAGES.CLEAR)}</button>}
      </InputGroup>
    }

    return (<div className="boolEditDisplay">
      {children || (value !== null ? this.renderCheckbox(value) : null) ||
       (defaultValue ? this.renderCheckbox(defaultValue) : null) ||
       intl.formatMessage(MESSAGES.NONE)}
    </div>);
  }

  renderAddon = () => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "textedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render() {
    return (
        <InputGroup className="boolEditContainer"
                    onMouseOver={this.handleMouseOver}
                    onMouseOut={this.handleMouseOut}>
          {this.renderInput()}{this.renderAddon()}
        </InputGroup>
    );
  }
}

export const BoolEdit = injectIntl(BoolEditComponent);